import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ShoppingBag, MessageCircle, Sparkles, Star } from "lucide-react";
import { LINKS, scrollToId } from "../data/site";
import { Sparkle } from "./Sparkle";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const floaters = [
  { top: "14%", left: "6%", size: 26, delay: 0, color: "text-pinky" },
  { top: "70%", left: "10%", size: 18, delay: 1.2, color: "text-babyblue" },
  { top: "22%", left: "88%", size: 20, delay: 0.6, color: "text-goldy" },
  { top: "78%", left: "82%", size: 28, delay: 1.8, color: "text-lilac" },
  { top: "46%", left: "3%", size: 14, delay: 2.4, color: "text-lilac" },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="inicio" ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden pb-20 pt-28 sm:pt-32">
      {/* fundo: céu pastel + blobs */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FFF0F6] via-[#F3E8FF] to-[#E6F4FE]" />
      <div className="absolute -left-32 top-24 -z-10 h-96 w-96 animate-floaty rounded-full bg-pinky/40 blur-[110px]" />
      <div className="absolute -right-24 top-1/2 -z-10 h-[28rem] w-[28rem] rounded-full bg-lilac/35 blur-[120px]" style={{ animationDelay: "1.4s" }} />
      <div className="absolute bottom-0 left-1/3 -z-10 h-72 w-72 rounded-full bg-babyblue/40 blur-[100px]" style={{ animationDelay: "2.6s" }} />

      {floaters.map((f, i) => (
        <span key={i} className={`pointer-events-none absolute animate-twinkle ${f.color}`} style={{ top: f.top, left: f.left, animationDelay: `${f.delay}s` }}>
          <Sparkle className="h-5 w-5 drop-shadow-[0_2px_6px_rgba(183,148,230,0.6)] sm:h-6 sm:w-6" />
        </span>
      ))}

      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:gap-8">
        {/* texto */}
        <motion.div style={{ y: yText }} className="text-center lg:text-left">
          <motion.p
            initial={{ opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
            className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border-2 border-pink-200 bg-white/80 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-inksoft shadow-[0_6px_18px_rgba(247,168,196,0.35)] backdrop-blur sm:text-[13px] lg:mx-0"
          >
            <Sparkles size={14} className="text-goldy" />
            Acessórios kawaii & geek feitos à mão
          </motion.p>

          <motion.p
            initial={{ opacity: 0, rotate: -8, y: 10 }}
            animate={{ opacity: 1, rotate: -4, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.45 }}
            className="font-hand text-3xl text-lilac sm:text-4xl"
          >
            bem-vindos ao
          </motion.p>

          <h1 className="font-display text-[clamp(3.4rem,11vw,8.5rem)] font-bold leading-[0.92] tracking-tight text-ink">
            <MaskedLine delay={0.55}>ANIME</MaskedLine>
            <MaskedLine delay={0.72}>
              <span className="bg-gradient-to-r from-pinky via-lilac to-babyblue bg-clip-text text-transparent">
                GLAM
              </span>
              <Sparkle className="ml-2 inline-block h-[0.42em] w-[0.42em] text-goldy align-baseline" />
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1 }}
            className="mx-auto mt-6 max-w-xl text-base font-medium leading-relaxed text-inksoft sm:text-lg lg:mx-0"
          >
            Bijuterias, agendas, canecas, quadros e mimos dos seus animes favoritos —
            tudo escolhido e produzido com carinho para o seu dia brilhar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-4 lg:justify-start"
          >
            <a
              href={LINKS.shopee}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-shopee-cta"
              className="group flex items-center gap-2.5 rounded-full bg-shopee px-7 py-4 text-base font-extrabold text-white shadow-[0_14px_34px_rgba(238,77,45,0.42)] transition-transform duration-300 hover:scale-105 hover:bg-shopeedark active:scale-95"
            >
              <ShoppingBag size={19} className="transition-transform duration-300 group-hover:-rotate-12" />
              Ver na Shopee
            </a>
            <a
              href={LINKS.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="flex items-center gap-2.5 rounded-full border-[2.5px] border-wa bg-white/80 px-7 py-4 text-base font-extrabold text-ink backdrop-blur transition-transform duration-300 hover:scale-105 hover:bg-wa hover:text-white active:scale-95"
            >
              <MessageCircle size={19} />
              Falar no WhatsApp
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-inksoft/80 lg:justify-start"
          >
            <Star size={15} className="fill-goldy text-goldy" />
            Entregamos fofura para todo o Brasil via Shopee
          </motion.p>
        </motion.div>

        {/* emblema do logo */}
        <motion.div style={{ y: yLogo }} className="relative mx-auto flex w-fit flex-col items-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 90, damping: 14, delay: 0.7 }}
            className="relative"
          >
            <div className="absolute inset-0 -z-10 scale-110 rounded-full bg-gradient-to-tr from-pinky/60 via-lilac/50 to-babyblue/60 blur-2xl" />

            <svg viewBox="0 0 100 100" className="absolute -inset-5 h-auto w-[calc(100%+2.5rem)] animate-spin-slow text-lilac/60" aria-hidden="true">
              <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="1.5 5" strokeLinecap="round" />
            </svg>

            <motion.div
              style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
              animate={{ y: [0, -12, 0] }}
              transition={{ y: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
              className="relative"
            >
              <img
                src="images/logo-anime-glam.jpg"
                alt="Logo Anime Glam by Camila Machado"
                className="h-64 w-64 rounded-full border-[6px] border-white object-cover shadow-[0_30px_70px_rgba(183,148,230,0.45)] sm:h-80 sm:w-80 lg:h-[380px] lg:w-[380px]"
                data-testid="hero-logo-badge"
              />

              <img
                src="images/produtos/pingentes.jpg"
                alt="Pingentes de celular da Anime Glam"
                className="absolute -left-16 top-8 hidden w-24 -rotate-[10deg] rounded-2xl border-4 border-white object-cover shadow-[0_14px_30px_rgba(45,30,62,0.25)] md:block lg:-left-24 lg:w-28"
                loading="eager"
              />
              <img
                src="images/produtos/pulseiras.jpg"
                alt="Pulseiras kawaii artesanais"
                className="absolute -right-14 bottom-10 hidden w-24 rotate-[8deg] rounded-2xl border-4 border-white object-cover shadow-[0_14px_30px_rgba(45,30,62,0.25)] md:block lg:-right-20 lg:w-28"
                loading="eager"
              />
            </motion.div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-7 font-hand text-3xl text-inksoft"
          >
            by Camila Machado
          </motion.p>
        </motion.div>
      </div>

      {/* indicador de scroll */}
      <motion.a
        href="#produtos"
        onClick={scrollToId("#produtos")}
        data-testid="hero-scroll-indicator"
        aria-label="Rolar para produtos"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-lilac md:flex"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <svg width="22" height="30" viewBox="0 0 22 30" fill="none" aria-hidden="true">
            <rect x="1.5" y="1.5" width="19" height="27" rx="9.5" stroke="currentColor" strokeWidth="2.5" />
            <circle cx="11" cy="10" r="2.5" fill="currentColor" />
          </svg>
        </motion.span>
      </motion.a>
    </section>
  );
}
